import { PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { PageTitleSubLarge } from 'packages/kakapo-ui-kit/components/common/Headings';
import { toRgbString } from 'packages/kakapo-ui-kit/helpers/HexColor';
import CloseIcon from 'packages/kakapo-ui-kit/assets/icons/CloseIcon';
import DragHamburgerIcon from 'packages/kakapo-ui-kit/assets/icons/DragHamburgerIcon';
import { DraggableProvided } from 'react-beautiful-dnd';

const Card = styled.div<{
	dragging: boolean;
}>`
	background: ${(props) => props.theme.colors.whiteBlue};
	border-radius: 5px;

	${(props) =>
		props.dragging &&
		`
	box-shadow: 0px 0px 0px 1px rgba(${toRgbString(props.theme.colors.slateBlue)}, 0.1),
			    0px 4px 8px rgba(${toRgbString(props.theme.colors.slateBlue)}, 0.25),
			    0px 32px 64px rgba(${toRgbString(props.theme.colors.slateBlue)}, 0.25);
	`}
`;

const Header = styled(Flex)`
	padding: 10px 19px;
	background: white;
	border: 1px solid #c2cad2;
	border-top-left-radius: 6px;
	border-top-right-radius: 6px;
`;

const Title = styled(PageTitleSubLarge)`
	color: ${(props) => props.theme.colors.slateBlue};
	font-size: 11px;
	text-transform: uppercase;
	font-weight: bold;
	flex-grow: 1;
`;

const EditButton = styled.div`
	cursor: pointer;
	display: flex;
	align-items: center;
	flex-shrink: 0;
	flex-grow: 0;
	cursor: pointer;
	color: ${(props) => props.theme.colors.primary};
	padding: 5px;
	margin: -5px;

	&:hover {
		background: rgba(${(props) => toRgbString(props.theme.colors.primary)}, 0.1);
		border-radius: 4px;

		&:active {
			background: rgba(${(props) => toRgbString(props.theme.colors.primary)}, 0.25);
		}
	}
`;

const Toolbar = styled(Flex)<{ hover: boolean }>`
	opacity: 0.25;
	transition: 0.4s ease-in-out opacity;

	${(props) => (props.hover ? `opacity: 1;` : undefined)}

	svg {
		display: block;
	}
`;

export default function SectionCard(
	props: PropsWithChildren<{
		id: string;
		section: number;
		dragHandleProps?: DraggableProvided['dragHandleProps'];
		dragging: boolean;
		onDelete: (id: string) => void;
	}>,
) {
	const [hover, setHover] = useState(false);
	const { t } = useTranslation();

	return (
		<Card
			dragging={props.dragging}
			onMouseDown={() => (document.activeElement as HTMLElement)?.blur()}
		>
			<Header
				{...props.dragHandleProps}
				alignItems="center"
				onMouseEnter={() => setHover(true)}
				onMouseLeave={() => setHover(false)}
			>
				<Title>
					{t('common.settings.evaluationForms.section.title')} #{props.section + 1}
				</Title>
				<Toolbar hover={hover} alignItems="center">
					<Box mx={3}>
						<EditButton onClick={() => props.onDelete(props.id)}>
							<CloseIcon color="red" />
						</EditButton>
					</Box>
					<DragHamburgerIcon color="slateBlue" />
				</Toolbar>
			</Header>
			{props.children}
		</Card>
	);
}
